import { AlertCircle, Lock } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { ColocaPhoneField } from "@/shared/components/forms/ColocaPhoneField";
import { ColocaSelectField } from "@/shared/components/forms/ColocaSelectField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { isOnboardingCountryIso3, withPreservedCountry } from "@/shared/data/onboarding-countries";
import { useCountryLabel } from "@/shared/hooks/useCountryLabel";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { colocaToast } from "@/shared/utils/toast";

import { COUNTRY_OPTIONS } from "../constants/clients.constants";
import { ClientsService } from "../services/clientsService";
import type { ClientByIdResponse, UpdateClientBody } from "../types/clients-api.types";
import { createEditClientContactSchema, type EditClientContactValues } from "../utils/editClientSchema";
import { clientDisplayName } from "../utils/normalizeClient";
import { EditFormFooter } from "./EditFormFooter";
import { KycStatusPill } from "./KycStatusPill";

export interface EditClientFormProps {
  client: ClientByIdResponse;
  onSuccess: () => void;
  onCancel: () => void;
  /**
   * Notifies the parent whenever the form's dirty state flips. Use this to gate close/discard prompts — e.g.
   * ask for confirmation before navigating away when there are unsaved changes.
   */
  onDirtyChange?: (isDirty: boolean) => void;
}

/**
 * Contact-only edit form. The identity (names, document, dates) is shared across tenants and managed by the
 * identities flow — PATCH /internal/clients/:id rejects identity fields, so they render as a locked read-only
 * block above the editable contact data.
 */
export function EditClientForm({ client, onSuccess, onCancel, onDirtyChange }: EditClientFormProps) {
  const { t } = useTranslations("clients");
  const countryLabel = useCountryLabel();
  // Onboarding allowlist, plus whatever this client already has stored — a record created before the gate
  // may sit on an excluded country, and dropping the option would blank the select over a live value.
  const countryOptions = useMemo(
    () =>
      withPreservedCountry(COUNTRY_OPTIONS, client.country, isOnboardingCountryIso3).map(
        ({ label, value }) => ({ label: countryLabel(value, label), value }),
      ),
    [client.country, countryLabel],
  );
  const schema = useMemo(() => createEditClientContactSchema((key) => t(key) as string), [t]);

  const defaults: EditClientContactValues = useMemo(
    () => ({
      email: client.email ?? "",
      phone: client.phone ?? "",
      country: client.country ?? "",
    }),
    [client],
  );

  const form = useZodForm(schema, defaults);
  const {
    formState: { isSubmitting, isDirty },
  } = form;

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (values: EditClientContactValues) => {
      setSubmitError(null);
      const diff = buildContactDiff(client, values);
      if (Object.keys(diff).length === 0) {
        colocaToast.info(t("edit_no_changes"));
        return;
      }
      try {
        await ClientsService.update(client.id, diff);
        colocaToast.success(t("edit_toast_success"));
        onSuccess();
      } catch (err) {
        const msg = HttpApiService.getErrorMessage(err) || t("edit_toast_error");
        setSubmitError(msg);
        colocaToast.error(t("edit_toast_error"));
      }
    },
    [client, onSuccess, t],
  );

  const documentLabel =
    client.identification_type && client.identification_number
      ? `${client.identification_type} ${client.identification_number}`
      : "—";
  const isBusiness = client.type === "business";
  const identityDate = isBusiness ? client.incorporation_date : client.birthdate;

  return (
    <ColocaForm form={form} onSubmit={handleSubmit}>
      {submitError && <ColocaAlert variant="destructive" icon={AlertCircle} text={submitError} />}

      <section className="rounded-xl border border-border bg-muted/30 p-4">
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          <Lock className="size-3.5" aria-hidden="true" />
          {t("edit_identity_title")}
        </div>
        <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
          <div>
            <dt className="text-xs text-muted-foreground">{t("edit_identity_full_name")}</dt>
            <dd className="text-sm font-medium text-foreground">{clientDisplayName(client)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">{t("edit_identity_document")}</dt>
            <dd className="text-sm font-medium text-foreground tabular-nums">{documentLabel}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">
              {isBusiness ? t("edit_identity_incorporation_date") : t("edit_identity_birthdate")}
            </dt>
            <dd className="text-sm font-medium text-foreground tabular-nums">
              {identityDate ? formatDateTime(identityDate) : "—"}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">{t("filters_label_kyc_status")}</dt>
            <dd className="mt-0.5">
              <KycStatusPill status={client.kyc_status} size="sm" />
            </dd>
          </div>
        </dl>
        <p className="mt-3 text-xs text-muted-foreground">{t("edit_identity_locked_caption")}</p>
      </section>

      <h3 className="text-sm font-semibold text-foreground">{t("edit_section_contact")}</h3>

      <ColocaInputField
        name="email"
        label={t("modal_label_email")}
        placeholder={t("modal_placeholder_email")}
        type="email"
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <ColocaPhoneField name="phone" label={t("modal_label_phone")} />
        <ColocaSelectField
          name="country"
          label={t("modal_label_country")}
          placeholder={t("modal_placeholder_country")}
          items={countryOptions}
        />
      </div>

      <EditFormFooter isSubmitting={isSubmitting} onCancel={onCancel} />
    </ColocaForm>
  );
}

function normalize(value: string | null | undefined): string {
  return (value ?? "").trim();
}

function buildContactDiff(client: ClientByIdResponse, values: EditClientContactValues): UpdateClientBody {
  const patch: UpdateClientBody = {};
  if (normalize(values.email) !== normalize(client.email)) patch.email = normalize(values.email);
  if (normalize(values.phone) !== normalize(client.phone)) patch.phone = normalize(values.phone);
  if (normalize(values.country) !== normalize(client.country)) patch.country = normalize(values.country);
  return patch;
}
